import { SerializedConstructor } from '@langchain/core/load/serializable';
import { File05Icon, Save01Icon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useBlocker } from 'react-router-dom';
import { mutate as mutateGlobal } from 'swr';

import { useEvaluatorStore } from '@/components/EvaluatorCrudPane/store/evaluatorStore';
import {
  processPromptManifest,
  reinsertFewShotToSerializedManifest,
} from '@/components/EvaluatorCrudPane/utils/fewShotExamplesUtils';
import { useCommitAndSaveRule } from '@/components/EvaluatorCrudPane/utils/useCommitAndSaveRule';
import { useHubTypeManifest } from '@/components/EvaluatorCrudPane/utils/useHubTypeManifest';
import { useVariableMappingProps } from '@/components/EvaluatorCrudPane/utils/useVariableMappingProps';
import useToast from '@/components/Toast/useToast';
import { FetcherParams } from '@/data/fetcher';
import { Button, IconButton, Tooltip } from '@/design-system';
import { useOrganizationId, useRunRules } from '@/hooks/useSwr.tsx';
import { mutateWithInfiniteRelative } from '@/hooks/useSwr.utils';
import { CommitManifestResponse, RuleSchema } from '@/types/schema';
import { hubApiReposPath } from '@/utils/constants';
import { useLocalStorageState } from '@/utils/use-local-storage-state.tsx';

import {
  SecretsProvider,
  useSecrets,
} from '../Playground/PlaygroundHome/PlaygroundSecretsContext';
import { useAlignEvaluatorInfo } from '../Playground/PlaygroundHome/hooks/useAlignEvaluator';
import {
  deepEqualIgnoreUndefined,
  useDeep,
  usePlaygroundStore,
} from '../Playground/PlaygroundHome/hooks/usePlaygroundStore';
import { usePlaygroundSubmit } from '../Playground/PlaygroundHome/hooks/usePlaygroundSubmit';
import { PlaygroundMainProps } from '../Playground/PlaygroundHome/types';
import { PlaygroundHeader } from '../Playground/components/PlaygroundHeader';
import { PlaygroundSecrets } from '../Playground/components/PlaygroundSecrets';
import { PlaygroundSubmit } from '../Playground/components/PlaygroundSubmit';
import {
  PLAYGROUND_SECRETS_SOURCE_LOCAL_STORAGE_KEY,
  PlaygroundSecretsSource,
} from '../Playground/components/constants';
import { PlaygroundParentContextProvider } from '../Playground/context/PlaygroundParentContext';
import {
  createPlaygroundManifest,
  storedManifestToPlaygroundManifestAndOptions,
} from '../Playground/utils/Playground.utils';
import { DEFAULT_NUM_FEW_SHOT_EXAMPLES } from '../Settings/utils/constants';
import { EvaluatorConfirmationModal } from './EvaluatorConfirmationModal';
import { EvaluatorPlaygroundLayout } from './EvaluatorPlaygroundLayout';
import { EVALUATOR_PLAYGROUND_BATCH_SIZE } from './constants';
import { useUpdateSessionPromptMetadata } from './hooks/useUpdateSessionPromptMetadata';
import { resolveHubRefCommitHash } from './utils/resolveHubRefCommitHash';

export type EvaluatorPlaygroundMainProps = PlaygroundMainProps & {
  evaluator: RuleSchema;
  onSaveEvaluator: (
    newCommit: CommitManifestResponse | undefined,
    newRule: RuleSchema | undefined
  ) => void;
};

export function EvaluatorPlaygroundMain(
  props: PlaygroundMainProps & EvaluatorPlaygroundMainProps
) {
  const initializeState = usePlaygroundStore((state) => state.initializeState);

  const [hasInitializedState, setHasInitializedState] = useState(false);
  const importEvaluator = useEvaluatorStore((state) => state.importEvaluator);
  const setName = useEvaluatorStore((state) => state.setName);
  const setFewShotEnabled = useEvaluatorStore(
    (state) => state.setFewShotEnabled
  );
  const setNumFewShotExamples = useEvaluatorStore(
    (state) => state.setNumFewShotExamples
  );
  const setPrompt = usePlaygroundStore((state) => state.setPrompt);

  const setExcludedEvaluatorIds = usePlaygroundStore(
    (state) => state.setExcludedEvaluatorIds
  );
  const setEvaluatorFeedbackKeys = usePlaygroundStore(
    (state) => state.setEvaluatorFeedbackKeys
  );
  const setFewShotContent = useEvaluatorStore(
    (state) => state.setFewShotContent
  );
  const rule = useRunRules({ dataset_id: props.datasetId });
  const ruleId = rule?.data?.[0]?.id;
  const hubPrompt = usePlaygroundStore((state) => state.prompts[0]?.hubPrompt);
  useEffect(() => {
    if (ruleId) {
      // Initialize with no excluded evaluators (all selected by default)
      setExcludedEvaluatorIds(new Set<string>());
    }
  }, [ruleId, setExcludedEvaluatorIds]);

  const initializeEvaluatorStore = useCallback(
    (evaluator: RuleSchema, fewShotContent?: string) => {
      importEvaluator(
        evaluator.evaluators?.[0]?.structured,
        'dataset',
        evaluator.backfill_from,
        evaluator.evaluator_version
      );
      setEvaluatorFeedbackKeys(['alignment']);
      setName(evaluator.display_name);
      setFewShotEnabled(evaluator?.use_corrections_dataset ?? false);
      setNumFewShotExamples(
        evaluator?.num_few_shot_examples ?? DEFAULT_NUM_FEW_SHOT_EXAMPLES
      );
      if (fewShotContent) {
        setFewShotContent(fewShotContent);
      }
    },
    [
      importEvaluator,
      setEvaluatorFeedbackKeys,
      setFewShotEnabled,
      setName,
      setNumFewShotExamples,
      setFewShotContent,
    ]
  );

  useEffect(() => {
    const { fewShotContent, processedManifest } = processPromptManifest(
      props.promptManifest
    );
    initializeEvaluatorStore(props.evaluator, fewShotContent);
    initializeState({
      ...props,
      promptManifest: processedManifest,
    });

    setHasInitializedState(true);
    // only want to run this on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSaveEvaluator = (
    newRule: RuleSchema,
    newCommit?: CommitManifestResponse
  ) => {
    props.onSaveEvaluator(newCommit, newRule);
    let fewShotContent: string | undefined;
    let processedManifest: SerializedConstructor | undefined;
    if (newCommit) {
      const { promptManifest, modelManifest } =
        storedManifestToPlaygroundManifestAndOptions(newCommit?.manifest);
      const processedPromptManifest = processPromptManifest(promptManifest);
      fewShotContent = processedPromptManifest.fewShotContent;
      processedManifest = processedPromptManifest.processedManifest;
      const newPromptManifest = createPlaygroundManifest(
        processedManifest,
        modelManifest
      );
      if (hubPrompt) {
        setPrompt(0, (prev) => ({
          ...prev,
          hubPrompt: {
            prompt: {
              ...hubPrompt.prompt,
              last_commit_hash: newCommit.commit_hash,
            },
            edited: false,
            commitHash: newCommit.commit_hash,
          },
          manifest: newPromptManifest,
        }));
      }
    }
    if (newRule) {
      initializeEvaluatorStore(newRule, fewShotContent);
    }
  };

  if (!hasInitializedState)
    return (
      <div className="mx-2 mt-4">
        <LinearProgress />
      </div>
    );

  return (
    <PlaygroundParentContextProvider isSimplified={false}>
      <SecretsProvider>
        <EvaluatorPlaygroundMainContent
          scrollRef={props.scrollRef}
          onSaveEvaluator={onSaveEvaluator}
        />
      </SecretsProvider>
    </PlaygroundParentContextProvider>
  );
}

function EvaluatorPlaygroundMainContent({
  scrollRef,
  onSaveEvaluator,
}: {
  scrollRef?: Dispatch<SetStateAction<HTMLDivElement | null>>;
  onSaveEvaluator: (
    newRule: RuleSchema,
    newCommit?: CommitManifestResponse
  ) => void;
}) {
  const organizationId = useOrganizationId();

  const datasetId = usePlaygroundStore((state) => state.datasetId);
  const isCreatingNewDataset = usePlaygroundStore(
    (state) => state.isCreatingNewDataset
  );
  const inputSourceType = usePlaygroundStore((state) => state.inputSourceType);

  const { secrets, setSecrets, openSecretsRef } = useSecrets();
  const [selectedSecretsSourceLS, setSelectedSecretsSource] =
    useLocalStorageState<PlaygroundSecretsSource>(
      PLAYGROUND_SECRETS_SOURCE_LOCAL_STORAGE_KEY,
      PlaygroundSecretsSource.BROWSER
    );

  // Confirmation modal state
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [dontShowConfirmationAgain, setDontShowConfirmationAgain] =
    useLocalStorageState('ls:evaluator_playground_confirmation', false);

  const selectedSecretsSource = organizationId
    ? selectedSecretsSourceLS
    : PlaygroundSecretsSource.BROWSER;

  const variableMappingProps = useVariableMappingProps();

  const onSubmit = usePlaygroundSubmit({
    selectedSecretsSource,
    variableMapping: variableMappingProps.variableMappings,
    batchSize: EVALUATOR_PLAYGROUND_BATCH_SIZE,
  });

  const { rule, originalDatasetId, originalSessionId } =
    useAlignEvaluatorInfo();
  const { createToast } = useToast();

  const prompt = usePlaygroundStore(useDeep((state) => state.prompts[0]));
  const { hubPrompt, manifest } = prompt;

  const { manifest: hubManifestOriginal } = useHubTypeManifest();
  const fewShotContent = useEvaluatorStore((state) => state.fewShotContent);

  const reinsertedManifest = useMemo(
    () => reinsertFewShotToSerializedManifest(manifest, fewShotContent),
    [manifest, fewShotContent]
  );
  const hasManifestChanged = useMemo(() => {
    return !deepEqualIgnoreUndefined(reinsertedManifest, hubManifestOriginal);
  }, [hubManifestOriginal, reinsertedManifest]);

  const hasVariableMappingChanged = useMemo(() => {
    return !deepEqualIgnoreUndefined(
      variableMappingProps.variableMappings,
      rule?.evaluators?.[0]?.structured?.variable_mapping
    );
  }, [variableMappingProps.variableMappings, rule?.evaluators]);

  const saveEvaluatorDisabled =
    (!originalDatasetId && !originalSessionId) ||
    !rule ||
    !organizationId ||
    !hubPrompt?.prompt?.repo_handle ||
    !hubPrompt?.prompt?.last_commit_hash ||
    !(hasManifestChanged || hasVariableMappingChanged);

  // Check if there are unsaved changes
  const hasUnsavedChanges =
    (hasManifestChanged || hasVariableMappingChanged) && !saveEvaluatorDisabled;

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (
      currentLocation.pathname === nextLocation.pathname ||
      dontShowConfirmationAgain
    ) {
      return false;
    }
    return hasUnsavedChanges;
  });

  // Handle blocked navigation
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setIsConfirmationModalOpen(true);
    }
  }, [blocker.state]);

  const onSuccessSavePrompt = useCallback(() => {
    mutateWithInfiniteRelative((key: FetcherParams) =>
      key?.url?.includes(hubApiReposPath)
    );
  }, []);

  const generateValidatedEvaluator = (commitHash: string | undefined) => {
    const newCommitHash = resolveHubRefCommitHash(
      rule?.evaluators?.[0]?.structured?.['hub_ref'],
      commitHash
    );

    return {
      hub_ref: `${hubPrompt?.prompt?.repo_handle}:${newCommitHash}`,
      variable_mapping: variableMappingProps.variableMappings ?? undefined,
    };
  };

  const { trigger: triggerUpdateSessionPromptMetadata } =
    useUpdateSessionPromptMetadata({});

  const onSuccess = useCallback(
    async (newRule: RuleSchema, commit?: CommitManifestResponse) => {
      const promises = [
        mutateGlobal((key: FetcherParams) => key?.url?.includes('commits')),
      ];
      if (commit) {
        promises.push(
          triggerUpdateSessionPromptMetadata(
            `${
              rule?.evaluators?.[0]?.structured?.['hub_ref'].split(':')[0]
            }:${commit?.commit_hash.slice(0, 8)}`,
            prompt?.sessionId
          ) // if theres no session id, then we dont have a baseline score, so we shouldn't set the metadata (when we pass undefined it's a no-op)
        );
      }
      await Promise.all(promises);
      onSaveEvaluator(newRule, commit);
    },
    [
      onSaveEvaluator,
      prompt?.sessionId,
      rule?.evaluators,
      triggerUpdateSessionPromptMetadata,
    ]
  );

  const getShouldMutateRule = useCallback(
    (commit: CommitManifestResponse | undefined) => {
      const newCommitHash = resolveHubRefCommitHash(
        rule?.evaluators?.[0]?.structured?.['hub_ref'],
        commit?.commit_hash
      );

      return (
        hasVariableMappingChanged ||
        `${hubPrompt?.prompt?.repo_handle}:${newCommitHash}` !==
          rule?.evaluators?.[0]?.structured?.['hub_ref']
      );
    },
    [
      hasVariableMappingChanged,
      hubPrompt?.prompt?.repo_handle,
      rule?.evaluators,
    ]
  );

  const { saveRule, isSavingEvaluator } = useCommitAndSaveRule({
    datasetId: originalDatasetId,
    sessionId: originalSessionId,
    existingRule: rule,
    transformManifest: undefined,
    onSuccessSavePrompt,
    onSuccess,
    hubRefManifest: reinsertedManifest,
    getShouldMutateRule,
  });

  const saveEvaluator = async () => {
    if (saveEvaluatorDisabled) {
      return;
    }
    const success = await saveRule({
      validatedEvaluator: generateValidatedEvaluator,
    });
    createToast({
      title: 'Evaluator saved',
      description: 'Your evaluator has been saved successfully',
    });
    return success;
  };

  // Handle save and continue from confirmation modal
  const handleSaveAndContinue = async () => {
    const success = await saveEvaluator();
    // On successful save, proceed with navigation
    if (isConfirmationModalOpen && success) {
      blocker?.proceed?.();
      setIsConfirmationModalOpen(false);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <EvaluatorConfirmationModal
        proceed={blocker?.proceed}
        isConfirmationModalOpen={isConfirmationModalOpen}
        setIsConfirmationModalOpen={(value) => {
          setIsConfirmationModalOpen(value);
          blocker?.reset?.();
        }}
        setDontShowConfirmationAgain={setDontShowConfirmationAgain}
        onSaveAndContinue={handleSaveAndContinue}
        isSaving={isSavingEvaluator}
      />
      <PlaygroundHeader
        rightActions={
          <div className="flex items-center gap-2">
            <a
              href={
                'https://docs.langchain.com/langsmith/improve-judge-evaluator-feedback#3-test-your-evaluator-prompt-against-the-labeled-examples'
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconButton
                color="secondary"
                variant="outlined"
                icon={File05Icon}
                label="Learn more about aligning your evaluators"
              />
            </a>
            <PlaygroundSecrets
              openSecretsRef={openSecretsRef}
              value={secrets}
              onChange={setSecrets}
              selectedSecretsSource={selectedSecretsSource}
              setSelectedSecretsSource={setSelectedSecretsSource}
            />
            <Tooltip
              title={
                'Saving changes to this evaluator will create a new commit on the associated hub prompt.'
              }
            >
              <Button
                onClick={saveEvaluator}
                size="sm"
                color="primary"
                variant="outlined"
                loading={isSavingEvaluator}
                disabled={saveEvaluatorDisabled}
                leftDecorator={Save01Icon}
              >
                Save Evaluator
              </Button>
            </Tooltip>
            <PlaygroundSubmit
              disabled={
                inputSourceType === 'dataset' &&
                !datasetId &&
                !isCreatingNewDataset
              }
              onSubmit={onSubmit}
            />
          </div>
        }
      />

      <EvaluatorPlaygroundLayout scrollRef={scrollRef} />
    </div>
  );
}
